import React from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import InvestmentBudgetGrid from './InvestmentBudgetGrid';
import InvestmentBudgetSummary from './InvestmentBudgetSummary';

import {
  completeInvestmentBudget,
  createInvestmentBudget,
  getInvestmentBudgetWorkbench,
  saveInvestmentBudgetLines,
  submitInvestmentBudget,
  validateInvestmentBudget,
} from './investmentbudget.apis';

import {
  InvestmentBudgetLine,
  InvestmentBudgetWorkbenchModel,
} from './investmentbudget.models';

import {
  PlanningPageProps,
  PlanningWorkbenchShell,
} from '../planningcore';

type State={
  loading:boolean;
  saving:boolean;

  model?:InvestmentBudgetWorkbenchModel;
  rows:InvestmentBudgetLine[];

  error?:string;
  success?:string;

  createOpen:boolean;
  name:string;

  submitOpen:boolean;
  notes:string;
};

export default class InvestmentBudgetWorkbench
extends React.Component<PlanningPageProps,State>{

  state:State={
    loading:false,
    saving:false,
    rows:[],
    createOpen:false,
    name:'',
    submitOpen:false,
    notes:'',
  };

  componentDidMount(){
    this.initialize();
  }

  componentDidUpdate(previous:PlanningPageProps){
    if(
      Number(previous.plancontext?.pci||0)!==
      Number(this.props.plancontext?.pci||0)
    ){
      this.initialize();
    }
  }

  private pci=()=>
    Number(this.props.plancontext?.pci||0);

  initialize=async()=>{
    const pci=this.pci();

    if(!pci){
      this.setState({
        error:'Planning Cycle context is missing.',
      });
      return;
    }

    this.setState({
      loading:true,
      error:undefined,
    });

    try{
      const model=
        await getInvestmentBudgetWorkbench(pci);

      this.setState({
        model,
        rows:model.lines,
      });
    }
    catch(error:any){
      this.setState({
        error:
          error?.message||
          String(error),
      });
    }
    finally{
      this.setState({
        loading:false,
      });
    }
  };

  create=async()=>{
    this.setState({
      saving:true,
      error:undefined,
      success:undefined,
    });

    try{
      await createInvestmentBudget(
        this.pci(),
        this.state.name,
      );

      this.setState({
        createOpen:false,
        name:'',
        success:
          'Investment Budget draft created.',
      });

      await this.initialize();
    }
    catch(error:any){
      this.setState({
        error:
          error?.message||
          String(error),
      });
    }
    finally{
      this.setState({
        saving:false,
      });
    }
  };

  save=async(rows:InvestmentBudgetLine[])=>{
    const header=this.state.model?.header;

    if(!header) return;

    this.setState({
      saving:true,
      error:undefined,
      success:undefined,
    });

    try{
      await saveInvestmentBudgetLines(
        header.headerid,
        header.stamp,
        rows,
      );

      this.setState({
        success:
          'Investment Budget draft saved. '
          +'You may continue editing later.',
      });

      await this.initialize();
    }
    catch(error:any){
      this.setState({
        error:
          error?.message||
          String(error),
      });
    }
    finally{
      this.setState({
        saving:false,
      });
    }
  };

  submit=async()=>{
    const header=this.state.model?.header;

    if(!header) return;

    this.setState({
      saving:true,
      error:undefined,
      success:undefined,
    });

    try{
      await validateInvestmentBudget(
        header.headerid,
      );

      const result=
        await submitInvestmentBudget
        (
          header.headerid,
          header.stamp,
          this.state.notes,
        );

      if(
        String(result?.state||'').toUpperCase()
        !=='SUBMITTED'
      ){
        throw new Error(
          'The server did not confirm the Submitted state.',
        );
      }

      this.setState({
        submitOpen:false,
        notes:'',
        success:
          'Investment Budget submitted '
          +'for consolidation.',
      });

      await this.initialize();
    }
    catch(error:any){
      this.setState({
        error:
          error?.message||
          String(error),
      });
    }
    finally{
      this.setState({
        saving:false,
      });
    }
  };

  complete=async()=>{
    this.setState({
      saving:true,
      error:undefined,
      success:undefined,
    });

    try{
      const result=
        await completeInvestmentBudget
        (
          this.pci(),
          this.state.notes,
        );

      const next=
        result?.actions?.nextpage
        ??result?.transition?.nextpage
        ??null;

      if(next){
        this.props.setPage(next);
      }
      else{
        await this.initialize();
      }
    }
    catch(error:any){
      this.setState({
        error:
          error?.message||
          String(error),
      });
    }
    finally{
      this.setState({
        saving:false,
      });
    }
  };

  render(){
    const model=this.state.model;
    const header=model?.header;
    const actions=model?.actions;

    const headerActions=(
      <Stack direction="row" spacing={1}>
        {actions?.canCreate&&(
          <Button
            startIcon={<AddIcon/>}
            variant="contained"
            onClick={()=>
              this.setState({
                createOpen:true,
              })
            }
          >
            Create Investment Budget
          </Button>
        )}

        {actions?.canSubmit&&(
          <Button
            variant="contained"
            onClick={()=>
              this.setState({
                submitOpen:true,
              })
            }
          >
            Submit
          </Button>
        )}

        {actions?.canComplete&&(
          <Button
            color="success"
            variant="contained"
            onClick={this.complete}
          >
            Complete Investment Budget
          </Button>
        )}
      </Stack>
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        title="Investment Budget"
        subtitle={
          'Prepare capital investments within '
          +'the active Investment Budget ceiling.'
        }
        icon={<AccountBalanceIcon/>}

        headerExtension={
          header
            ?<Chip
               size="small"
               label={header.statename}
             />
            :undefined
        }

        summary={
          header
            ?<InvestmentBudgetSummary
               value={header}
             />
            :undefined
        }

        actions={headerActions}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.initialize}
      >
        {!header&&model&&!actions?.canCreate&&(
          <Alert severity="warning">
            Activate a Budget Control with an
            Investment Budget ceiling before
            creating the Investment Budget.
          </Alert>
        )}

        {header&&(
          <InvestmentBudgetGrid
            rows={this.state.rows}
            costcenters={model?.costcenters||[]}
            accounts={model?.accounts||[]}
            categories={model?.categories||[]}
            editable={!!actions?.canEdit}
            saving={this.state.saving}
            onRowsChange={rows=>
              this.setState({rows})
            }
            onSave={this.save}
          />
        )}

        <Dialog
          open={this.state.createOpen}
          onClose={()=>
            this.setState({
              createOpen:false,
            })
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Create Investment Budget
          </DialogTitle>

          <DialogContent>
            <TextField
              sx={{mt:1}}
              fullWidth
              label="Budget name"
              value={this.state.name}
              onChange={event=>
                this.setState({
                  name:event.target.value,
                })
              }
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={()=>
                this.setState({
                  createOpen:false,
                })
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={this.create}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={this.state.submitOpen}
          onClose={()=>
            this.setState({
              submitOpen:false,
            })
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Submit Investment Budget
          </DialogTitle>

          <DialogContent>
            <TextField
              sx={{mt:1}}
              fullWidth
              multiline
              minRows={3}
              label="Submission notes"
              value={this.state.notes}
              onChange={event=>
                this.setState({
                  notes:event.target.value,
                })
              }
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={()=>
                this.setState({
                  submitOpen:false,
                })
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={this.submit}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </PlanningWorkbenchShell>
    );
  }
}
